import User, { RoleType } from '../models/User'
import { AdminPermission, AdminPermissionKey } from '../constants/adminPermissions'

const FULL_ADMIN_ROLES = [RoleType.admin, RoleType.sadmin]

export function isFullAdmin(user?: User | null): boolean {
  return !!user?.role && FULL_ADMIN_ROLES.includes(user.role)
}

export function isRbacStaff(user?: User | null): boolean {
  return user?.role === RoleType.rbac
}

export function getEffectivePermissions(user?: User | null): AdminPermissionKey[] {
  if (!user) return []
  if (isFullAdmin(user)) {
    return Object.values(AdminPermission) as AdminPermissionKey[]
  }
  if (isRbacStaff(user) && Array.isArray(user.permissions)) {
    return user.permissions.filter((p): p is AdminPermissionKey =>
      Object.values(AdminPermission).includes(p as AdminPermissionKey),
    )
  }
  return []
}

export function rbacRequiresTransactionPassword(user?: User | null): boolean {
  if (!isRbacStaff(user)) return false
  const perms = user?.permissions || []
  return perms.includes(AdminPermission.DEPOSIT) || perms.includes(AdminPermission.WITHDRAW)
}

export function hasPermission(user: User | null | undefined, permission: AdminPermissionKey): boolean {
  if (!user) return false
  if (isFullAdmin(user)) return true
  if (isRbacStaff(user)) {
    return getEffectivePermissions(user).includes(permission)
  }
  if (user.role && user.role !== RoleType.user) return true
  return false
}

export function canAccessFinancialDashboard(user?: User | null): boolean {
  if (!user) return false
  if (isFullAdmin(user)) return true
  if (!isRbacStaff(user) && user.role !== RoleType.user) return true
  return (
    hasPermission(user, AdminPermission.DEPOSIT) ||
    hasPermission(user, AdminPermission.WITHDRAW) ||
    hasPermission(user, AdminPermission.ACCOUNT_STATEMENT) ||
    hasPermission(user, AdminPermission.PROFIT_LOSS)
  )
}

/** Map admin route path segment to required permission */
const ROUTE_PERMISSION_MAP: Array<{ test: (segment: string) => boolean; permission: AdminPermissionKey }> = [
  { test: (s) => s === 'event-management' || s === 'inplay-matches', permission: AdminPermission.EVENT_MANAGEMENT },
  { test: (s) => s.startsWith('odds/'), permission: AdminPermission.EVENT_MANAGEMENT },
  { test: (s) => s === 'market-analysis', permission: AdminPermission.MARKET_ANALYSIS },
  { test: (s) => s.startsWith('list-clients') || s.startsWith('add-user'), permission: AdminPermission.USERS },
  { test: (s) => s === 'affiliates', permission: AdminPermission.AFFILIATES },
  { test: (s) => s === 'bonus', permission: AdminPermission.BONUS },
  { test: (s) => s === 'payment-method', permission: AdminPermission.PAYMENT_METHOD },
  { test: (s) => s === 'undeclared-bets', permission: AdminPermission.RESULT_DECLARATION },
  { test: (s) => s === 'analysis-pl', permission: AdminPermission.ADMIN_PL },
  { test: (s) => s === 'accountstatement', permission: AdminPermission.ACCOUNT_STATEMENT },
  { test: (s) => s === 'profitloss', permission: AdminPermission.PROFIT_LOSS },
  { test: (s) => s === 'unsettledbet/deleted', permission: AdminPermission.DELETED_BETS },
  { test: (s) => s.startsWith('unsettledbet'), permission: AdminPermission.CURRENT_BETS },
  { test: (s) => s === 'game-reports', permission: AdminPermission.GAME_REPORTS },
  { test: (s) => s === 'depositstatement', permission: AdminPermission.DEPOSIT },
  { test: (s) => s === 'withdrawstatement', permission: AdminPermission.WITHDRAW },
  { test: (s) => s === 'ledger', permission: AdminPermission.LEDGER },
  { test: (s) => s === 'user-book', permission: AdminPermission.USER_BOOK },
  { test: (s) => s === 'security-monitoring', permission: AdminPermission.SECURITY_MONITORING },
  { test: (s) => s.startsWith('sports-list') || s.startsWith('active-matches') || s.startsWith('active-markets') || s.startsWith('active-fancies'), permission: AdminPermission.BLOCK_MARKETS },
  { test: (s) => s.startsWith('series/') || s.startsWith('matches/'), permission: AdminPermission.ADD_MATCH },
  { test: (s) => s === 'messages', permission: AdminPermission.MESSAGES },
  { test: (s) => s === 'whatsapp-settings', permission: AdminPermission.WHATSAPP },
  { test: (s) => s.startsWith('casino-list') || s.startsWith('casino/'), permission: AdminPermission.CASINO_LIST },
  { test: (s) => s === 'casino-visibility', permission: AdminPermission.CASINO_VISIBILITY },
  { test: (s) => s === 'series-settings', permission: AdminPermission.SERIES_SETTINGS },
  { test: (s) => s === 'betdelay', permission: AdminPermission.BET_DELAY },
  { test: (s) => s.startsWith('match-settings'), permission: AdminPermission.MATCH_SETTINGS },
  { test: (s) => s === 'rbac-users', permission: AdminPermission.RBAC_MANAGE },
]

const PUBLIC_ADMIN_SEGMENTS = new Set(['rules', 'terms', 'change-password', 'unauthorized', 'login'])

export function canAccessRoute(user: User | null | undefined, pathSegment: string): boolean {
  const segment = (pathSegment || '').replace(/\/$/, '')
  if (!segment) return !isRbacStaff(user) || hasPermission(user, AdminPermission.EVENT_MANAGEMENT)
  if (PUBLIC_ADMIN_SEGMENTS.has(segment)) return true
  if (!isRbacStaff(user)) return true
  const rule = ROUTE_PERMISSION_MAP.find((r) => r.test(segment))
  if (!rule) return false
  return hasPermission(user, rule.permission)
}

const ROUTE_PRIORITY: Array<{ path: string; permission: AdminPermissionKey }> = [
  { path: '/', permission: AdminPermission.EVENT_MANAGEMENT },
  { path: '/list-clients', permission: AdminPermission.USERS },
  { path: '/depositstatement', permission: AdminPermission.DEPOSIT },
  { path: '/withdrawstatement', permission: AdminPermission.WITHDRAW },
  { path: '/accountstatement', permission: AdminPermission.ACCOUNT_STATEMENT },
  { path: '/unsettledbet', permission: AdminPermission.CURRENT_BETS },
  { path: '/affiliates', permission: AdminPermission.AFFILIATES },
  { path: '/bonus', permission: AdminPermission.BONUS },
  { path: '/payment-method', permission: AdminPermission.PAYMENT_METHOD },
  { path: '/ledger', permission: AdminPermission.LEDGER },
  { path: '/sports-list/active-matches', permission: AdminPermission.BLOCK_MARKETS },
  { path: '/undeclared-bets', permission: AdminPermission.RESULT_DECLARATION },
  { path: '/market-analysis', permission: AdminPermission.MARKET_ANALYSIS },
  { path: '/analysis-pl', permission: AdminPermission.ADMIN_PL },
]

/** Absolute admin panel URL — use with <Navigate>; use navigate.go() with paths from getFirstAllowedRoute instead */
export function resolveAdminPanelPath(path: string): string {
  if (!path || path === '/') return '/admin'
  const normalized = path.startsWith('/') ? path : `/${path}`
  if (normalized.startsWith('/admin')) return normalized.replace(/\/+$/, '') || '/admin'
  return `/admin${normalized}`
}

export function getFirstAllowedRoute(user?: User | null): string {
  if (!user) return '/login'
  if (!isRbacStaff(user)) return '/'
  for (const { path, permission } of ROUTE_PRIORITY) {
    if (hasPermission(user, permission)) return path
  }
  const perms = getEffectivePermissions(user)
  if (perms.length) {
    const fallback = ROUTE_PRIORITY.find((r) => perms.includes(r.permission))
    if (fallback) return fallback.path
  }
  return '/unauthorized'
}
